"use client";

import { useIsStoreOpen } from "@/hooks/use-is-store-open";
import Link from "next/link";
import { Navigation, type NavigationProps } from "@/components/navigation";
import { useStore } from "@/contexts/store";
import { Button, IconButton } from "@stone-payments/jade";
import { WhatsAppIconButton } from "@/components/whatsapp-icon-button";
import { productAnalitica } from "@/app/(store)/_utils/events";
import { useExperiment } from "@/contexts/analitica";
import { ExperimentsFlags } from "@/services/amplitude/experiment";
import { NavigationCartButton } from "@/components/navigation-cart-button";

type HeaderProps = {
  actions?: {
    search?: boolean;
    whatsApp?:
      | boolean
      | Pick<
          Parameters<typeof WhatsAppIconButton>[0],
          "className" | "analiticaEvent"
        >;
    cart?: {
      analiticaEvent?: Parameters<
        typeof NavigationCartButton
      >[0]["analiticaEvent"];
    };
  };
  navigation?: {
    homeLink: Partial<NavigationProps["homeLink"]>;
  };
  collapsed?: boolean;
};

function Header({ actions, navigation, collapsed }: HeaderProps) {
  const { store, enabledWhatsAppSharing } = useStore();
  const aboutLinkExperiment = useExperiment(ExperimentsFlags.AboutHeaderLink);
  const showCartExperiment = useExperiment(ExperimentsFlags.ShowCart);
  const { isOpen: isStoreOpen, flagOn: isStoreOpenFlagOn } = useIsStoreOpen();
  let actionComponents: React.ReactNode[] = [];

  if (actions?.search) {
    actionComponents = [
      ...actionComponents,
      <Link
        href={`/${store.identifier}`}
        key="search"
        onClick={() => productAnalitica.events.action.searchClicked()}
        aria-label="Buscar"
      >
        <IconButton
          as="span"
          icon="action-search"
          variant="on-brand-solid"
          size="medium"
          className="xl:!h-12 xl:!p-6 xl:!leading-0"
        />
      </Link>,
    ];
  }

  if (aboutLinkExperiment.key === "on") {
    actionComponents = [
      ...actionComponents,
      <Link href={`/${store.identifier}/sobre`} key="about" aria-label="Sobre">
        <Button
          as="span"
          variant="on-brand-solid"
          size="large"
          icon="shop"
          className="!hidden xl:!flex"
        >
          Sobre
        </Button>

        <IconButton
          as="span"
          variant="on-brand-solid"
          size="medium"
          icon="shop"
          className="!flex xl:!hidden"
        />
      </Link>,
    ];
  }

  if (actions?.whatsApp && enabledWhatsAppSharing) {
    actionComponents = [
      ...actionComponents,
      <div
        key="whatsapp"
        className="ml-auto flex gap-jade-100 md:ml-0 md:gap-0"
      >
        <WhatsAppIconButton
          name={store.name}
          whatsAppNumber={store.vitrine.whatsAppNumber}
          {...(typeof actions.whatsApp === "boolean" ? {} : actions.whatsApp)}
        />

        {showCartExperiment.key === "on" ? (
          <NavigationCartButton key="cart" {...(actions.cart || {})} />
        ) : null}
      </div>,
    ];
  }

  return (
    <Navigation
      actions={actionComponents}
      homeLink={{
        href: `/${store.identifier}`,
        ...navigation?.homeLink,
      }}
      title={store.name}
      logoUrl={store.vitrine.brandLogo?.url}
      backgroundUrl={store.vitrine.backgroundImage?.url}
      collapsed={collapsed}
      applyTheme={!!store.vitrine.applyThemeHeader && !!store.vitrine.theme}
      isOpen={isStoreOpen}
      showOpenTag={isStoreOpenFlagOn}
    />
  );
}

export { Header, type HeaderProps };
